import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Chip } from '@mui/material'
import { Add } from '@mui/icons-material'
import { PageHeader, Button, DataTable, StatusChip, Card, PermissionGate } from '@components/common'
import { WorkOrderForm } from '@components/maintenance'
import { toast } from 'react-toastify'
import api from '@services/api'

const priorityColors = {
  low: 'default',
  medium: 'info',
  high: 'warning',
  critical: 'error',
}

const columns = [
  { field: 'wo_number', headerName: 'WO #', sortable: true, width: 120 },
  { field: 'title', headerName: 'Title', sortable: true },
  { field: 'asset_name', headerName: 'Asset', sortable: true },
  {
    field: 'priority',
    headerName: 'Priority',
    sortable: true,
    renderCell: (value) => (
      <Chip
        label={value}
        size="small"
        color={priorityColors[value] || 'default'}
        sx={{ textTransform: 'capitalize' }}
      />
    ),
  },
  {
    field: 'status',
    headerName: 'Status',
    sortable: true,
    renderCell: (value) => <StatusChip status={value} />,
  },
  {
    field: 'due_date',
    headerName: 'Due Date',
    sortable: true,
    renderCell: (value) => value ? new Date(value).toLocaleDateString() : '-',
  },
  { field: 'assigned_to_name', headerName: 'Assigned To', sortable: true },
]

const WorkOrdersList = () => {
  const navigate = useNavigate()
  const [workOrders, setWorkOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)

  const fetchWorkOrders = async () => {
    try {
      setLoading(true)
      const response = await api.get('/maintenance/work-orders/')
      setWorkOrders(response.data.results || response.data)
    } catch (error) {
      console.error('Error fetching work orders:', error)
      toast.error('Failed to load work orders')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchWorkOrders()
  }, [])

  const handleFormSuccess = () => {
    fetchWorkOrders()
  }

  return (
    <Box>
      <PageHeader
        title="Work Orders"
        subtitle="Manage maintenance work orders"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Work Orders' },
        ]}
        actions={
          <PermissionGate permission="work_orders.create">
            <Button startIcon={<Add />} onClick={() => setFormOpen(true)}>
              Create Work Order
            </Button>
          </PermissionGate>
        }
      />

      <Card>
        <DataTable
          columns={columns}
          data={workOrders}
          loading={loading}
          onRowClick={(row) => navigate(`/work-orders/${row.id}`)}
          searchPlaceholder="Search work orders..."
          emptyMessage="No work orders found"
        />
      </Card>

      <WorkOrderForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSuccess={handleFormSuccess}
      />
    </Box>
  )
}

export default WorkOrdersList
